import { SearchModel } from './common';

/**
 * Tipos para Pedidos de Compra conforme schema [Financial].[PurchaseOrder]
 * 
 * Status (Situação):
 *   0 = Em aberto
 *   1 = Atendido
 *   2 = Cancelado
 *   3 = Em andamento
 *
 * FreightResponsibility (fretePorConta):
 *   0 = Remetente
 *   1 = Destinatário
 *
 * DiscountUnit:
 *   REAL       = desconto em valor absoluto
 *   PERCENTUAL = desconto em percentual
 */

// ─── Enums ───────────────────────────────────────────────────────

export enum PurchaseOrderStatus {
  EmAberto = 0,
  Atendido = 1,
  Cancelado = 2,
  EmAndamento = 3,
}

export const PURCHASE_ORDER_STATUS_OPTIONS = [
  { value: 0, label: 'Em Aberto', color: 'blue' },
  { value: 1, label: 'Atendido', color: 'green' },
  { value: 2, label: 'Cancelado', color: 'red' },
  { value: 3, label: 'Em Andamento', color: 'yellow' },
] as const;

export enum FreightResponsibility {
  Remetente = 0,
  Destinatario = 1,
}

export const FREIGHT_RESPONSIBILITY_OPTIONS = [
  { value: 0, label: 'Remetente (CIF)' },
  { value: 1, label: 'Destinatário (FOB)' },
] as const;

export const DISCOUNT_UNIT_OPTIONS = [
  { value: 'REAL', label: 'R$ (Valor)' },
  { value: 'PERCENTUAL', label: '% (Percentual)' },
] as const;

// ─── Payment Method ──────────────────────────────────────────────

export interface PaymentMethod {
  payment_method_id: number;
  payment_method_name: string;
}

// ─── Payment Method Model ────────────────────────────────────────

export interface PaymentMethodModel {
  payment_method_model_id: number;
  payment_method_model_name: string;
}

// ─── Purchase Order Item ─────────────────────────────────────────

export interface PurchaseOrderItem {
  purchase_order_item_id?: number;
  purchase_order_id?: number;
  description: string;
  sku?: string;           // SKU do produto (somente leitura, retornado pelo GET)
  supplier_code?: string; // Código do fornecedor (somente leitura, carregado de ProviderProductPrice)
  unit?: string;
  unit_value: number;
  quantity: number;
  ipi_rate?: number;
  detailed_description?: string;
  bling_product_id?: number;
  product_id?: number;
  raw_material_id?: number;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  last_updated_by?: string;
}

/** Extensão local para rastrear linhas no formulário */
export interface PurchaseOrderItemRow extends PurchaseOrderItem {
  _tempId: string; // id temporário para controle de UI
  _type: 'product' | 'raw_material'; // tipo do item
  _sourceCode?: string; // SKU do produto ou código da matéria-prima
}

// ─── Purchase Order Installment ──────────────────────────────────

export interface PurchaseOrderInstallment {
  purchase_order_installment_id?: number;
  purchase_order_id?: number;
  value: number;
  due_date: string; // ISO date
  notes?: string;
  payment_method_id?: number;
  payment_method_name?: string; // join label
  /** Base do vencimento pela condição do fornecedor: 1 emissão, 2 entrega. Ausente = data fixa. */
  due_date_base?: 1 | 2 | null;
  /** Dias somados à base. */
  due_days?: number | null;
  /** Cartão da empresa que paga a parcela — a provisão vai para a fatura dele. */
  credit_card_id?: number | null;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  last_updated_by?: string;
}

/** Cartão da empresa no seletor da parcela paga com cartão. */
export interface PurchaseCreditCard {
  credit_card_id: number;
  company_id: number;
  company_name: string;
  description: string;
}

/** Extensão local para rastrear parcelas no formulário */
export interface PurchaseOrderInstallmentRow extends PurchaseOrderInstallment {
  _tempId: string;
}

// ─── Purchase Order (cabeçalho) ──────────────────────────────────

export interface PurchaseOrder {
  /** Avisos não fatais do save (parcela paga com cartão que não foi para a fatura). */
  warnings?: string[];
  purchase_order_id: number;
  bling_id?: number;
  order_number?: number;
  order_date?: string;
  expected_date?: string;
  total_products?: number;
  total?: number;
  company_id?: number;
  company_name?: string;         // join label para exibição
  company_document?: string;     // join label para exibição (CNPJ)
  provider_id?: number;
  provider_name?: string;        // join label para exibição
  provider_document?: string;    // join label para exibição
  status: number;
  purchase_order_reference?: string;
  notes?: string;
  internal_notes?: string;
  discount_value?: number;
  discount_unit?: string;        // 'REAL' | 'PERCENTUAL'
  category_id?: number;
  /** Categoria financeira (AccountingPlan.ErpId) herdada pelos lançamentos provisionados das parcelas. */
  accounting_plan_id?: number;
  total_icms?: number;
  total_ipi?: number;
  freight_cost?: number;
  carrier?: string;
  freight_responsibility?: number;
  gross_weight?: number;
  volumes?: number;
  payment_method_model_id?: number;
  payment_method_model_name?: string; // join label
  is_priority?: boolean;         // pedido de compra prioritário (badge/ordenado primeiro)
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  last_updated_by: string;
  // sub-resources (retornado pelo GET :id)
  items?: PurchaseOrderItem[];
  installments?: PurchaseOrderInstallment[];
  sales_orders?: PurchaseOrderSalesOrderLink[]; // PVs atendidos por este PC (N:N)
}

/** Pedido de venda vinculado ao PC, enriquecido para exibição na tela do PC. */
export interface PurchaseOrderSalesOrderLink {
  order_id: number;
  customer_name?: string;
  order_status_id?: number;
  order_status?: string;
  is_priority: boolean;
  request_delivery_time?: string; // data necessária (timestamp Brasil)
  scheduled_ship_date?: string;   // YYYY-MM-DD
}

// ─── Form Data ───────────────────────────────────────────────────

export interface PurchaseOrderFormData {
  order_number?: number;
  order_date?: string;
  expected_date?: string;
  company_id?: number;
  provider_id?: number;
  status: number;
  purchase_order_reference?: string;
  notes?: string;
  internal_notes?: string;
  discount_value?: number;
  discount_unit?: string;
  category_id?: number;
  /** Categoria financeira (AccountingPlan.ErpId) das provisões geradas pelas parcelas. */
  accounting_plan_id?: number;
  total_icms?: number;
  total_ipi?: number;
  freight_cost?: number;
  carrier?: string;
  freight_responsibility?: number;
  gross_weight?: number;
  volumes?: number;
  payment_method_model_id?: number;
  is_priority?: boolean;
  is_active: boolean;
  items: PurchaseOrderItemRow[];
  installments: PurchaseOrderInstallmentRow[];
  sales_order_ids?: number[]; // PVs vinculados (replace-all no backend)
}

/**
 * Pré-preenchimento do formulário de PC (location.state.prefill), vindo do
 * Planejamento de Compras — "Pedido para X meses" (RN-EST-75).
 */
export interface PurchaseOrderPrefill {
  provider_id: number;
  provider_name: string;
  internal_notes?: string;
  items: Array<{
    product_id: number;
    sku: string;
    description: string;
    quantity: number;
    unit_value: number;
    unit?: string;
  }>;
}

// ─── Search Model ────────────────────────────────────────────────

export interface PurchaseOrderSearchModel extends SearchModel {
  status?: number;
  provider_id?: number;
  company_id?: number;
  is_active?: boolean;
  is_priority?: boolean;
  date_from?: string;
  date_to?: string;
  /** false = prioritários não ficam fixos no topo (ordenação por data de criação). */
  priority_first?: boolean;
}

// ─── Provider Product (busca de produtos por fornecedor) ─────────

/** Produto retornado pela API de busca por fornecedor, com preço de custo específico */
export interface ProviderProduct {
  product_id: number;
  sku: string;
  description: string;
  supplier_code?: string;  // código do fornecedor (de ProviderProductPrice)
  provider_cost: number;   // preço de custo específico do fornecedor
  price?: number;          // preço de venda do produto (referência)
  unit?: string;
  is_active?: boolean;
  is_custom_product?: boolean; // habilita as fotos do recebimento
}

// ─── Resumo da listagem ──────────────────────────────────────────

/**
 * Contadores por situação da listagem INTEIRA (não só da página), com os mesmos filtros da
 * listagem exceto o de situação — senão filtrar por "Em aberto" zeraria os outros cartões.
 */
export interface PurchaseOrderStatusSummary {
  open: number;        // 0 = Em aberto
  in_progress: number; // 3 = Em andamento
  completed: number;   // 1 = Atendido
  cancelled: number;   // 2 = Cancelado
}
